<template>
  <div class="p-field">
    <label :for="id" class="form-label">
      {{ label }}
      <span v-if="isRequired" class="color-danger">(required)</span>
    </label>

    <component
      :is="inputComponent"
      :modelValue="modelValue"
      :id="id"
      v-bind="inputAttrs"
      :class="['form-control', { 'p-invalid': showError }]"
      :aria-invalid="showError"
      :aria-describedby="showError ? errorId : null"
      :aria-required="isRequired"
      @blur="onBlur"
      @focus="$emit('focus', $event)"
      @update:modelValue="(v) => emit('update:modelValue', v)"
    />

    <small v-if="showError" :id="errorId" class="p-error color-danger">
      {{ firstErrorMessage }}
    </small>
  </div>
</template>

<script setup>
import {computed, toRefs, watch, onMounted} from 'vue';

const props = defineProps({
  modelValue: [String, Number, Date, Object, Array],
  validation: {
    type: Object,
    required: false,
  },
  label: {
    type: String,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  inputComponent: {
    type: [Object, String],
    default: 'InputText',
  },
  inputAttrs: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue', 'blur', 'focus']);

// Normalize validation into a plain (unwrapped) Vuelidate node for consistent access
const { modelValue, validation } = toRefs(props);

const validationNode = computed(() => {
  // If the prop is a ref to a Vuelidate node (has $touch) return its value
  if (validation?.value && typeof validation.value.$touch === 'function') return validation.value;
  // If the prop itself is a raw Vuelidate node return it
  if (props.validation && typeof props.validation.$touch === 'function') return props.validation;
  // otherwise no validation available
  return null;
});

const isRequired = computed(() => {
  const v = validationNode.value || props.validation;
  return !!(v && Object.prototype.hasOwnProperty.call(v, 'required'));
});

const errorId = computed(() => `${props.id}-error`);

const firstErrorMessage = computed(() => {
  const v = validationNode.value || props.validation;
  if (v && Array.isArray(v.$errors) && v.$errors.length > 0) {
    return v.$errors[0].$message || 'Invalid value';
  }
  return '';
});

const showError = computed(() => {
  const v = validationNode.value || props.validation;
  // Show error if there are any $errors (useful after programmatic $validate),
  // or if the node reports $error and is $dirty (user-focused validation).
  if (!v) return false;
  if (Array.isArray(v.$errors) && v.$errors.length > 0) return true;
  return !!(v.$error && v.$dirty);
});

function callValidate() {
  const v = validationNode.value || props.validation;
  if (v && typeof v.$touch === 'function') {
    v.$touch();
  }
}

function onBlur(e) {
  callValidate();
  emit('blur', e);
}

// Debug logging to assist runtime investigation (safe, can be removed later)
if (process.env.NODE_ENV !== 'production') {
  watch(
    () => validationNode.value && Array.isArray(validationNode.value.$errors) ? validationNode.value.$errors.map(e => e.$message) : null,
    (errs) => {
      console.debug('[ValidatedInput] validation $errors for', props.id, errs);
    },
    { immediate: true }
  );

  onMounted(() => {
    console.debug('[ValidatedInput] mounted for', props.id, {
      validationNode: validationNode.value,
      firstErrorMessage: firstErrorMessage.value,
      showError: showError.value,
    });
  });
}

</script>

<style scoped>
.p-error {
  display: block;
  margin-top: 0.25rem;
}
</style>
